import { useState, useEffect, useRef, useCallback } from "react";

interface NewChatTitleInputProps {
	onSubmit: (title: string) => Promise<void>;
	onCancel: () => void;
	isCreating: boolean;
}

/**
 * Name-your-chat step.
 *
 * Displayed in the main chat area (replacing the empty state) when the user
 * clicks "New Chat". Collects a title before the session is created so that
 * every session has a meaningful name from the outset.
 */
export default function NewChatTitleInput({
	onSubmit,
	onCancel,
	isCreating,
}: NewChatTitleInputProps) {
	const [title, setTitle] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	// Auto-focus the input when the component mounts
	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	// Escape key cancels, Enter submits (when valid)
	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === "Escape") {
				onCancel();
			} else if (event.key === "Enter" && title.trim() && !isCreating) {
				event.preventDefault();
				onSubmit(title.trim());
			}
		},
		[title, isCreating, onCancel, onSubmit],
	);

	const handleSubmit = useCallback(
		(event: React.FormEvent) => {
			event.preventDefault();
			const trimmed = title.trim();
			if (!trimmed || isCreating) return;
			onSubmit(trimmed);
		},
		[title, isCreating, onSubmit],
	);

	const isSubmittable = title.trim().length > 0 && !isCreating;

	return (
		<div className="new-chat-title-overlay">
			<div className="new-chat-title-card" role="dialog" aria-modal="true" aria-labelledby="new-chat-title-heading">
				<h2 id="new-chat-title-heading" className="new-chat-title-heading">
					Name your chat
				</h2>
				<p className="new-chat-title-hint">
					Give this conversation a clear name so you can find it later.
				</p>

				<form onSubmit={handleSubmit} className="new-chat-title-form" noValidate>
					<input
						ref={inputRef}
						type="text"
						className="new-chat-title-input"
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder="e.g. Sprint review discussion"
						maxLength={120}
						disabled={isCreating}
						autoComplete="off"
						spellCheck={true}
						aria-label="Chat title"
					/>

					<div className="new-chat-title-char-count" aria-live="polite" aria-atomic="true">
						{title.length}/120
					</div>

					<div className="new-chat-title-actions">
						<button
							type="button"
							className="new-chat-title-cancel-btn"
							onClick={onCancel}
							disabled={isCreating}
						>
							Cancel
						</button>
						<button
							type="submit"
							className="new-chat-title-btn"
							disabled={!isSubmittable}
							aria-busy={isCreating}
						>
							{isCreating ? (
								<>
									<span className="new-chat-title-spinner" aria-hidden="true" />
									Creating…
								</>
							) : (
								"Start Chat"
							)}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
